import { gql } from '@apollo/client/core';

export const PlaceWhiteListFragment = gql`
  fragment PlaceWhiteListFragment on placeWhiteLists {
    id
    ip
    alias
    placeID
  }
`;

export const PlaceFragment = gql`
  fragment PlaceFragment on qx_Place {
    id
    qxCustomerID
    accountNumber
    name
    type
    path
    canSelfServe
    requireTwoFactor
    faceTombstoneDays
    authProvider {
      id
      customerPlaceID
      provider
      sso
      sync
      syncedByPersonID
      syncedSetupBy {
        avatar_md
        id
        firstName
        lastName
        email
      }
      lastSynced
      error
      config
    }
    defaultView {
      id
      name
    }
    timezone
    isLockedDown
    parentPlaceID
    parentPlace {
      id
      name
      hierarchy {
        name
      }
      type
      path
      timezone
    }
    addresses
    # permissions {
    #   id
    # }
    gateway {
      # ...GatewayFragment
      id
      name
      connectionStatus
      syncStatus
      type
      place {
        id
      }
    }
    toHomePlaces {
      customerPlaceID
      customerPlace {
        id
        name
        type
        accountNumber
        anniversaryDate
        billingPeriodMonths
        canSelfServe
      }
      homePlace {
        id
        name
        type
        path
        accountNumber
        anniversaryDate
        billingPeriodMonths
        canSelfServe
      }
      integratorPlaceID
    }
    children {
      id
      name
      type
      path
      gateway {
        # ...GatewayFragment
        id
        name
        connectionStatus
        syncStatus
        type
      }
      addresses
      parentPlace {
        id
        name
        type
      }
      children {
        id
        path
        gateway {
          # ...GatewayFragment
          id
          name
          connectionStatus
          syncStatus
          type
        }
        addresses
      }
    }
    whitelists {
      ...PlaceWhiteListFragment
    }
    aiBillingTier
    aiOverageBudgetCents
  }
  ${PlaceWhiteListFragment}
`;

const LiveScrollerPlaceResolveFragment = gql`
  fragment LiveScrollerPlaceResolveFragment on qx_Place {
    id
    name
    path
    hierarchy {
      name
    }
    gateway {
      id
      name
      type
    }
  }
`;

const PlaceSearchFragment = gql`
  fragment PlaceSearchFragment on qx_Place {
    id
    name
    hierarchy {
      name
    }
  }
`;

export const PlaceBillingFragment = gql`
  fragment PlaceBillingFragment on qx_Place {
    anniversaryDate
    billingStartDate
    billingPeriodMonths
  }
`;

export const GQL_UPDATE_PLACE_BY_ID = gql`
  mutation GQL_UPDATE_PLACE_BY_ID($id: String!, $data: qx_Place_set_input!) {
    updatePlaces(where: { id: { _eq: $id } }, _set: $data) {
      returning {
        id
        name
        aiBillingTier
        aiOverageBudgetCents
        canSelfServe
        faceTombstoneDays
        pendingBillingTier
        pendingBillingTierStartsAt
        parentPlace {
          id
          name
          type
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const GQL_CANCEL_PENDING_BILLING_DOWNGRADE = gql`
  mutation GQL_CANCEL_PENDING_BILLING_DOWNGRADE($placeID: String!) {
    cancelPendingBillingDowngrade(placeID: $placeID) {
      success
    }
  }
`;

/**
 * Used by state-store to subscribe to the places starting at the
 * User's current scope. Used by the Ui to determine whether we
 * should refresh the UI on the User or log them out.
 */
const StateStoreSubscribeToPlaceById = gql`
  subscription StateStoreSubscribeToPlaceById($id: String!) {
    place(id: $id) {
      id
      name
      type
      path
      isLockedDown
    }
  }
`;

export const GQL_PLACE_SEARCH = gql`
  query GQL_PLACE_SEARCH($where: qx_Place_bool_exp, $limit: Int, $offset: Int, $orderBy: [qx_Place_order_by!]) {
    places(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PlaceSearchFragment
    }
    placeAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${PlaceSearchFragment}
`;

export const GQL_GET_PLACE_IDS_BY_ID = gql`
  query GQL_GET_PLACE_IDS_BY_ID($where: qx_Place_bool_exp, $limit: Int) {
    places(limit: $limit, where: $where) {
      id
    }
  }
`;

/**
 * Used for checking unique values under a customer,
 * but we first have to find the Customer Place ID.
 */
export const GQL_GET_CUSTOMER_PLACE_ID = gql`
  query GQL_GET_CUSTOMER_PLACE_ID($placeID: String!) {
    place(id: $placeID) {
      id
      toHomePlaces {
        homePlace {
          id
          path
          isLockedDown
          canSelfServe
        }
        customerPlaceID
        integratorPlaceID
        customerPlace {
          id
          path
          name
          type
          isLockedDown
          canSelfServe
        }
      }
    }
  }
`;

/**
 * Query a Single Place by ID
 */
export const GQL_GET_PLACE_BY_ID = gql`
  query GQL_GET_PLACE_BY_ID($id: String!) {
    place(id: $id) {
      ...PlaceFragment
      ...PlaceBillingFragment
    }
  }
  ${PlaceFragment}
  ${PlaceBillingFragment}
`;

export const GQL_GET_PLACES_BY_IDS = gql`
  query GQL_GET_PLACES_BY_IDS($ids: [String!]!) {
    places(where: { id: { _in: $ids } }) {
      ...LiveScrollerPlaceResolveFragment
    }
  }
  ${LiveScrollerPlaceResolveFragment}
`;

/**
 * Subscribe to a Single Place by ID
 */
export const GQL_SUB_PLACE_BY_ID = gql`
  subscription GQL_SUB_PLACE_BY_ID($id: String!) {
    place(id: $id) {
      ...PlaceFragment
      ...PlaceBillingFragment
    }
  }
  ${PlaceFragment}
  ${PlaceBillingFragment}
`;

export const GQL_GET_PLACE_HOME_BY_ID = gql`
  query GQL_GET_PLACE_HOME_BY_ID($id: String!) {
    place(id: $id) {
      id
      toHomePlaces {
        homePlace {
          id
          type
          name
          canSelfServe
          path
        }
        customerPlaceID
        customerPlace {
          id
          type
          name
          canSelfServe
          path
        }
        integratorPlaceID
        integratorPlace {
          id
          type
          name
          path
        }
        placeID
        place {
          id
          type
          name
          path
        }
        sitePlaceID
        sitePlace {
          id
          type
          name
          path
        }
      }
    }
  }
`;
